import React, { Suspense, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { rem } from '@vimeo/bokeh';
import { DefaultNavigation } from '@vimeo/default-navigation';
import { Ribbon } from '@vimeo/iris/layout';
import CreatePageTeamMemberPrivilegesBanner from '@vimeo/ui/core/CreatePageTeamMemberPrivilegesBanner';
import FBCreativeContextBanner from '@vimeo/ui/core/FBCreativeContextBanner';
import EssentialFooter from '@vimeo/ui/core/footer/EssentialFooter';
import MinimalFooter from '@vimeo/ui/core/footer/MinimalFooter';
import StandardFooter from '@vimeo/ui/core/footer/StandardFooter';
import StorageQuotaWarningBanner from '@vimeo/ui/core/StorageQuotaWarningBanner';
import { useClientRect } from '@vimeo/ui/core/utils/use-client-rect';

const GlobalNotifications = React.lazy(() =>
  import('@vimeo-notifications/global').then(({ Container }) => ({
    default: Container,
  })),
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const HeaderContainer = styled.div<{ isFixed: boolean }>`
  width: 100%;
  ${(props) =>
    props.isFixed &&
    css`
      position: fixed;
      z-index: 100;
    `};
`;

const Content = styled.div<{ offset: number }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-top: ${(props) => {
    return props.offset ? `${rem(props.offset)}` : 'auto';
  }};
`;

export type HeaderOption = 'default' | 'fixed' | 'none';
export const HeaderOption = { default: 'default', fixed: 'fixed', none: 'none' } as const;

export type FooterOption = 'default' | 'minimal' | 'none' | 'essential';
export const FooterOption = {
  default: 'default',
  minimal: 'minimal',
  none: 'none',
  essential: 'essential',
} as const;

function Footer({ option, viewer }: { option?: FooterOption; viewer: any }) {
  switch (option) {
    case FooterOption.minimal:
      return <MinimalFooter {...viewer} />;

    case FooterOption.default:
      return <StandardFooter {...viewer} />;

    case FooterOption.essential:
      return <EssentialFooter {...viewer} />;

    case FooterOption.none:
    default:
      return null;
  }
}

interface LayoutOptions {
  header?: HeaderOption;
  globalNotifications?: boolean;
  footer?: FooterOption;
  ribbon?: boolean;
  globalSearch?: boolean;
}

interface Props {
  children: React.ReactNode;
  options: LayoutOptions;
  viewer: any;
  hasThemeSupport?: boolean;
}

export const AppLayout = ({ options, viewer, children, hasThemeSupport }: Props) => {
  const hasHeader = options.header !== HeaderOption.none;
  const hasFixedHeader = options.header === HeaderOption.fixed;
  const [headerRect, headerRef] = useClientRect(['height'], [hasHeader]);
  const contentOffset = hasHeader && hasFixedHeader && headerRect !== null ? headerRect.height : 0;
  const [showFBCreativeContextBanner, setFBCreativeContextBannerVisibility] = useState(false);
  const [showTeamsPrivilegeBanner, setShowTeamsPrivilegeBanner] = useState(false);

  useEffect(() => {
    /**
     * This is a header component which is to be shown to users coming in from FB business manager context
     * to vimeo create and then wanders away from create pages.
     * Not to be shown on vimeo create pages.
     */
    const path = window.location.pathname;
    setFBCreativeContextBannerVisibility(
      document.cookie.indexOf('create_fb_context=1') !== -1 &&
        path.indexOf('/create') === -1 &&
        path.indexOf('/preview') === -1,
    );
  }, [viewer]);

  useEffect(() => {
    setShowTeamsPrivilegeBanner(
      viewer &&
        viewer.teamUser &&
        window.location.pathname.indexOf('/create/templates') !== -1 &&
        !window.location.pathname.match(/\/apps\/[0-9]+/), // Partner flows shouldn't see the banner
    );
  }, [viewer]);

  return (
    <Container>
      <HeaderContainer ref={headerRef} isFixed={hasFixedHeader}>
        <StorageQuotaWarningBanner />
        {options.globalNotifications && showFBCreativeContextBanner && <FBCreativeContextBanner />}
        {options.globalNotifications && viewer && viewer.xsrft ? (
          <Suspense fallback={null}>
            <GlobalNotifications token={viewer.xsrft} />
          </Suspense>
        ) : null}
        {showTeamsPrivilegeBanner && (
          <CreatePageTeamMemberPrivilegesBanner
            teamUserRole={viewer.teamUser.plainTextPermissionLevel}
            userId={viewer.user.id}
            locale={viewer.locale}
            vuid={viewer.vuid}
          />
        )}
        {options.ribbon ? (
          <Ribbon
            variant={viewer && viewer.ribbonModifier}
            animate={false}
            style={{ height: rem(3) }}
          />
        ) : null}
        {hasHeader && <DefaultNavigation hasThemeSupport={hasThemeSupport} />}
      </HeaderContainer>
      <Content offset={contentOffset}>{children}</Content>
      <Footer option={options.footer} viewer={viewer} />
    </Container>
  );
};
